// External libraries
import { Component } from "react";

// Preact Signals
import { effect } from "@preact/signals-react";

// Kendo React components
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { Error, Hint } from "@progress/kendo-react-labels";
import { Ripple } from "@progress/kendo-react-ripple";

// Internal components
import { If } from "../../Common/UI/IfComponent";
import { TooltipPopupIcon } from "../../Common/UI/TooltipPopupIcon";

// Utilities
import { validators } from "../../Form/validators";
import { Invisible, validateStageStep } from "../../Utilities/EntryUtils";

// Signals
import {
  clearEntryFields,
  disabledSections,
  entryValues,
  formErrors,
  readOnlyEntry,
  template,
} from "../../Entry/Manage/EditEntrySignals";

export class TimeField extends Component {
  constructor({
    field,
    handleFieldChange,
    sectionIndex,
    fieldIndex,
    labelVisible,
    eventIndex,
    value,
    galleryParentName,
    isStageField,
    isPrintPreview,
  }) {
    super();
    const { fieldName, properties } = field;
    this.state = {
      value: new Date(value),
      valid: this.validate(value, properties?.IsRequired),
      invisible: false,
      disabled: false,
      touched: false,
    };
    this.fieldName = typeof eventIndex === "number" ? `${fieldName}_${eventIndex}` : fieldName;
    this.labelVisible = labelVisible;
    this.isRequired = properties?.IsRequired;
    this.isReadOnly = properties?.IsReadOnly;
    this.hint = properties?.Hint;
    this.fieldInformation = properties?.FieldInformation;
    this.defaultValue = properties?.DefaultValue;
    this.visibilityRules = properties?.VisibilityRules;
    this.parameterValues = properties?.ParameterValues;
    this.label = properties?.Label || `${fieldName}:`;
    this.sectionIndex = sectionIndex;
    this.fieldIndex = fieldIndex;
    this.eventIndex = eventIndex;
    this.handleFieldChange = handleFieldChange;
    this.galleryParentName = galleryParentName;
    this.isStageField = isStageField;
    this.isPrintPreview = isPrintPreview;
    this.sectionName = template.value?.sections?.[sectionIndex]?.sectionName;
    this.validationMessage = "Error: A time is required.";
    this.disposeValidationEffect = () => {};
    this.disposeDependencyEffect = () => {};
    this.disposeClearFieldsEffect = () => {};
    this.disposeDisabledSectionsEffect = () => {};
  }

  //#region lifecycle functions
  componentDidMount() {
    this.handleClearFieldEffect();
    if (this.isRequired) {
      if (!this.validate(this.state.value, this.isRequired)) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
      this.handleValidationEffect();
      this.updateFormErrors();
    }
    if (this.visibilityRules) this.handleDependencyEffect();

    this.disposeDisabledSectionsEffect = effect(() => {
      const isSectionDisabled = disabledSections.value[this.sectionName];
      if (this.state.disabled !== isSectionDisabled) {
        this.setState({ disabled: isSectionDisabled });
      }
    });
  }

  componentWillUnmount() {
    this.disposeValidationEffect();
    this.disposeDependencyEffect();
    this.disposeClearFieldsEffect();
    this.disposeDisabledSectionsEffect();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      this.state.value !== nextState.value ||
      this.state.valid !== nextState.valid ||
      this.state.invisible !== nextState.invisible ||
      this.state.disabled !== nextState.disabled ||
      this.state.touched !== nextState.touched
    );
  }

  getSnapshotBeforeUpdate(_, prevState) {
    return {
      visibilityChange: prevState.invisible && !this.state.invisible,
    };
  }

  componentDidUpdate(_, __, snapshot) {
    if (snapshot.visibilityChange) this.setDefaultValue();
    if (this.state.invisible) this.clearField();
    if (!this.state.invisible && this.isRequired) {
      this.setState(
        (prevState) => ({ valid: this.validate(prevState.value, this.isRequired) }),
        () => {
          this.updateFormErrors();
        }
      );
    }
    if (this.state.disabled) this.clearFormErrors();
  }
  //#endregion lifecycle functions

  //#region custom functions
  validate(value, isRequired) {
    if (isRequired) {
      return validators.time(value);
    }
    return true;
  }

  handleClearFieldEffect() {
    this.disposeClearFieldsEffect = effect(() => {
      if (clearEntryFields.value) {
        if (this.defaultValue) {
          const newTime = new Date();
          const timeZoneOffset = newTime.getTimezoneOffset() * 60000;
          let adjustedDate = new Date(newTime.getTime() - timeZoneOffset);
          adjustedDate.setMilliseconds(0);
          adjustedDate = adjustedDate.toISOString().replace("Z", "");
          if (entryValues.value[this.fieldName] !== newTime) {
            this.setState(
              {
                value: newTime,
              },
              () => {
                this.handleFieldChange({
                  e: { target: { name: this.fieldName }, value: adjustedDate },
                  sectionIndex: this.sectionIndex,
                  fieldIndex: this.fieldIndex,
                  eventIndex: this.eventIndex,
                });
              }
            );
          }
        } else if (entryValues.value[this.fieldName] !== "") {
          this.setState({ value: null });
          this.handleFieldChange({
            e: { target: { name: this.fieldName }, value: null },
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            eventIndex: this.eventIndex,
          });
        }
      }
    });
  }

  handleValidationEffect() {
    this.disposeValidationEffect = effect(() => {
      this.setState({
        valid: formErrors.value[this.sectionName]
          ? formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1
          : true,
      });
    });
  }

  updateFormErrors() {
    if (!this.state.valid) {
      if (
        formErrors.value[this.sectionName]?.indexOf(this.fieldName) === -1 ||
        !formErrors.value[this.sectionName]
      ) {
        const newFormErrors = { ...formErrors.value };
        newFormErrors[this.sectionName] = newFormErrors[this.sectionName] || [];
        newFormErrors[this.sectionName].push(this.fieldName);
        formErrors.value = newFormErrors;
      }
    } else {
      this.clearFormErrors();
    }
  }

  clearFormErrors() {
    const newFormErrors = { ...formErrors.value };
    const sectionErrors = newFormErrors[this.sectionName];
    if (sectionErrors) {
      newFormErrors[this.sectionName] = sectionErrors.filter((error) => error !== this.fieldName);
    }
    formErrors.value = newFormErrors;
  }

  handleDependencyEffect() {
    this.disposeDependencyEffect = effect(() => {
      const result = Invisible({
        VisibilityRules: this.visibilityRules,
        parameterValues: this.parameterValues,
        eventIndex: this.eventIndex,
        galleryName: this.galleryParentName,
      });

      this.setState({
        invisible: result.invisible && !result.disabled,
        disabled: result.invisible && result.disabled,
      });
    });
  }

  clearField() {
    this.clearFormErrors();
    this.setState({ value: "" });
    this.handleFieldChange({
      e: { target: { name: this.fieldName }, value: "" },
      sectionIndex: this.sectionIndex,
      fieldIndex: this.fieldIndex,
      eventIndex: this.eventIndex,
    });
  }

  setDefaultValue() {
    if (this.defaultValue && !Date.parse(this.state.value)) {
      const newTime = new Date();
      const timeZoneOffset = newTime.getTimezoneOffset() * 60000;
      const adjustedDate = new Date(newTime.getTime() - timeZoneOffset)
        .toISOString()
        .replace("Z", "");
      this.setState(
        {
          value: new Date(newTime),
        },
        () => {
          this.handleFieldChange({
            e: { target: { name: this.fieldName }, value: adjustedDate },
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            eventIndex: this.eventIndex,
          });
        }
      );
    }
  }

  onChange(e) {
    if (!this.isPrintPreview) {
      if (e.value === null || e.value === "") {
        this.setState(
          {
            valid: this.validate(e.value, this.isRequired),
            value: e.value,
            touched: true,
          },
          () => {
            this.updateFormErrors();
            this.handleFieldChange({
              e: { ...e, value: "" },
              sectionIndex: this.sectionIndex,
              fieldIndex: this.fieldIndex,
              eventIndex: this.eventIndex,
            });
            if (this.isStageField) validateStageStep();
          }
        );
        return;
      }

      const newTime = new Date(e.value);
      const timeZoneOffset = newTime.getTimezoneOffset() * 60000;
      const adjustedDate = new Date(newTime.getTime() - timeZoneOffset)
        .toISOString()
        .replace("Z", "");
      this.setState(
        {
          touched: true,
          valid: this.validate(newTime, this.isRequired),
          value: newTime,
        },
        () => {
          this.updateFormErrors();
          this.handleFieldChange({
            e: { ...e, value: adjustedDate },
            sectionIndex: this.sectionIndex,
            fieldIndex: this.fieldIndex,
            eventIndex: this.eventIndex,
          });
          if (this.isStageField) validateStageStep();
        }
      );
    }
  }

  onBlur() {
    this.setState((prevState) => ({
      valid: this.validate(prevState.value, this.isRequired),
      touched: true,
    }));
    this.updateFormErrors();
    if (this.isStageField) validateStageStep();
  }

  getClassName() {
    if (this.isRequired) {
      if (this.state.valid) {
        return "!k-valid";
      } else {
        return "!k-invalid";
      }
    }
  }
  //#endregion custom functions

  render() {
    if (this.state.invisible) return null;
    return (
      <Ripple>
        <div className="k-d-flex k-flex-col">
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <TimePicker
              data-testid={this.fieldName}
              className={this.getClassName()}
              id={this.fieldName}
              name={this.fieldName}
              label={this.labelVisible ? this.label : null}
              onChange={(e) => this.onChange(e)}
              onBlur={() => this.onBlur()}
              value={isNaN(this.state.value) ? null : this.state.value}
              valid={this.isRequired ? this.state.valid : true}
              disabled={this.isReadOnly || this.state.disabled || readOnlyEntry.value}
            />
            <If
              condition={
                this.fieldInformation !== undefined &&
                this.fieldInformation !== "" &&
                this.fieldInformation !== "<p></p>"
              }
            >
              <TooltipPopupIcon content={this.fieldInformation} />
            </If>
          </div>
          <If condition={this.hint}>
            <Hint>{this.hint}</Hint>
          </If>
          <If condition={!this.state.valid && this.state.touched}>
            <Error>{this.validationMessage}</Error>
          </If>
        </div>
      </Ripple>
    );
  }
}
